import React, { type KeyboardEvent, useCallback, useState } from 'react'
import classNames from 'classnames'
import styles from './DataTypesInput.module.scss'

interface DataTypesInputProps {
  dataTypes: string[]
  onChange: (dataTypes: string[]) => void
  isInvalid?: boolean
}

export const DataTypesInput: React.FC<DataTypesInputProps> = ({ dataTypes, onChange, isInvalid = false }) => {
  const [draft, setDraft] = useState<string>('')

  const commit = useCallback(
    (value: string) => {
      const added = value
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '')
      const unique = added.filter((entry, index) => !dataTypes.includes(entry) && added.indexOf(entry) === index)
      if (unique.length > 0) {
        onChange([...dataTypes, ...unique])
      }
      setDraft('')
    },
    [dataTypes, onChange],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault()
        commit(draft)
        return
      }
      if (event.key === 'Backspace' && draft === '' && dataTypes.length > 0) {
        onChange(dataTypes.slice(0, -1))
      }
    },
    [commit, draft, dataTypes, onChange],
  )

  const pending = draft.trim()
  const alreadyAdded = pending !== '' && dataTypes.includes(pending)

  const handleRemove = useCallback(
    (dataType: string) => {
      onChange(dataTypes.filter((existing) => existing !== dataType))
    },
    [dataTypes, onChange],
  )

  const field = (
    <div
      className={classNames(
        'd-flex flex-wrap align-items-center zocks-gap-0.375 px-2 py-2 rounded-1 zocks-bg-color-white',
        styles.field,
        isInvalid ? 'zocks-border-color-danger' : 'border zocks-border-color-gray-400',
      )}
    >
      {dataTypes.map((dataType) => (
        <span
          key={dataType}
          className='d-inline-flex align-items-center zocks-gap-0.375 px-2 py-1 rounded-1 zocks-bg-color-primary-light zocks-text-primary paragraph-small'
        >
          {dataType}
          <button
            type='button'
            aria-label={`Remove ${dataType}`}
            onClick={() => handleRemove(dataType)}
            className={classNames('border-0 p-0 bg-transparent zocks-text-primary', styles.remove)}
          >
            <i className='bi bi-x' />
          </button>
        </span>
      ))}
      <input
        type='text'
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => commit(draft)}
        placeholder={dataTypes.length === 0 ? 'Type and press Enter to add…' : 'Add another…'}
        aria-label='Add a data type'
        className={classNames('border-0 flex-grow-1 paragraph-medium', styles.input)}
      />
    </div>
  )

  return (
    <div>
      {field}
      {pending === '' ? null : (
        <div className='mt-1 rounded-1 border zocks-border-color-gray-300 zocks-bg-color-white overflow-hidden'>
          {alreadyAdded ? (
            <span className='d-block px-2 py-2 paragraph-small zocks-text-gray-600'>
              <i className='bi bi-check2 me-1' />‘{pending}’ is already added
            </span>
          ) : (
            <button
              type='button'
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => commit(pending)}
              className={classNames('d-block w-100 text-start border-0 bg-transparent px-2 py-2 paragraph-small', styles.add)}
            >
              <i className='bi bi-plus-lg me-1' />
              Add ‘{pending}’
            </button>
          )}
        </div>
      )}
    </div>
  )
}
